<script setup lang="ts">
import { CategoryMdw, ProductMdw } from '~/lib/services/middleware/middleware.dtos'

const props = defineProps<{
  category: CategoryMdw
  categories: CategoryMdw[]
  origin: string
}>()

const route = useRoute()
const { t } = useLocale()
const { staticProductRoute } = useMcdRoutes()

const isCombo = computed(() => props.category.combo)

const comboSections = computed(() => {
  if (!isCombo) return undefined
  const sections: Record<string, ProductMdw[]> = {}
  props.category.products.forEach(p => {
    const subsection = p.subsection!
    if (sections[subsection]?.length) sections[subsection].push(p)
    else sections[subsection] = [p]
  })
  return sections
})

const seoExtraText = computed(() => props.category.infoDescription)

const getComboParentCategoryName = (parentCategoryId: string | undefined) => {
  if (!isCombo.value || !parentCategoryId) return undefined
  const matchedParentCat = props.categories.find(c => c._id === parentCategoryId)
  return matchedParentCat?.name || undefined
}

useJsonld(() => {
  if (!props.category) return null
  const category = props.category
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'url': `${props.origin}${route.path}`,
    'name': t('SEO.menu.category.title', { category: category.name }),
    'description': t('SEO.menu.category.description', { category: category.name }),
    '@itemListElement': category.products.map((prod, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'item': {
        name: prod.name,
        url: `${props.origin}${staticProductRoute(
          isCombo.value ? getComboParentCategoryName(prod.category) || '' : category.name,
          prod.name
        )}`,
      },
    })),
  }
})
</script>

<template>
  <section class="m-5 md:m-14">
    <header class="my-4 md:my-8">
      <h1 class="text-2xl font-bold md:text-3xl">{{ category.name }}</h1>
    </header>
    <template v-if="!isCombo">
      <div class="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 md:gap-10 xl:grid-cols-5">
        <StaticCategoryViewItem
          v-for="product in category.products"
          :key="product._id"
          :product="product"
          :category-title="category.name"
          :is-combo="false"
        />
      </div>
    </template>

    <template v-else-if="comboSections">
      <section v-for="[section, products] in Object.entries(comboSections)" :key="section">
        <h2 class="my-4 text-lg font-bold md:my-6 md:text-xl">{{ section }}</h2>

        <div class="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 md:gap-10 xl:grid-cols-5">
          <StaticCategoryViewItem
            v-for="product in products"
            :key="product._id"
            :product="product"
            :category-title="category.name"
            :is-combo="true"
            :combo-parent-category="getComboParentCategoryName(product.category || undefined)"
          />
        </div>
      </section>
    </template>
  </section>

  <!-- SEO text (infoDescription optional in model) -->
  <p
    v-if="seoExtraText"
    class="my-10 bg-mcd-secondaryIvory p-10 text-sm md:px-24 md:py-20 md:text-lg"
  >
    {{ seoExtraText }}
  </p>
</template>
